import { observable, computed, action } from "mobx";
import localStorage from "mobx-localstorage";
import _ from "lodash";

import oneToOne from "../assets/1to1.png";
import oneToMany from "../assets/1tomany.png";
import many from "../assets/many.png";
import one from "../assets/1.png";

export default class GraphicalRepresentationStore {
  constructor(http) {
    this.http = http;
  }

  districtsOptions = {
    DISTRICT_DISTANCE: 500,
    MIN_SIZE: 250,
    MAX_SIZE: 1000,
  };

  @observable
  visibleItems = [];

  @observable
  typeSelected = "";

  @observable
  searchExpr = "";

  @observable
  graph = {
    nodes: [],
    edges: [],
  };

  baseGraph = {
    nodes: [],
    edges: [],
  };

  @observable
  showDetails = false;

  @observable
  activeNode = [];

  @observable
  activeEdge = "";

  @observable groups =
    localStorage.getItem("graphical-representation-groups") !== undefined
      ? localStorage.getItem("graphical-representation-groups")
      : null;

  @computed
  get getVisibleItems() {
    return [...this.visibleItems];
  }

  @computed
  get getActiveNode() {
    return this.activeNode;
  }
  @computed
  get getActiveEdge() {
    return this.activeEdge;
  }

  @action
  resetBaseGraph = () => {
    this.baseGraph = {
      nodes: [],
      edges: [],
    };
  };

  @action
  setGroups = (groups) => {
    this.groups = groups;
    if (groups !== null)
      localStorage.setItem("graphical-representation-groups", this.groups);
  };

  @action
  setTypeSelected = (typeSelected) => {
    this.typeSelected = typeSelected;
  };
  @action
  setSearchExpr = (searchExpr) => {
    this.searchExpr = searchExpr;
  };

  @action
  setShowDetails = (showDetails) => {
    this.showDetails = showDetails;
  };

  @action
  setProgress = (progress) => {
    this.progress = progress;
  };

  @action
  setVisibleItems = (visibleItems) => {
    this.visibleItems = visibleItems;
  };
  @action
  setActiveNode = (activeNode) => {
    this.activeNode = activeNode;
  };
  @action
  setActiveEdge = (activeEdge) => {
    this.activeEdge = activeEdge;
  };

  @action
  setGraph = async (cubes, relationships, hierachical) => {
    if (this.baseGraph.nodes.length > 0) {
      const includedItems = this.baseGraph.nodes.filter((item) =>
        this.visibleItems.includes(item.id)
      );
      const graph = {
        nodes: includedItems,
        edges: this.graph.edges,
      };
      this.graph = graph;
      return this.graph;
    } else {
      const graph = {
        nodes: this.getNodes(cubes, hierachical, relationships),
        edges: this.getEdges(relationships),
      };
      this.baseGraph = graph;
      this.graph = graph;
      return graph;
    }
  };

  getNodePoint = (
    center,
    regionSize,
    nodeIndex,
    totNodesInDepth,
    nodeDepth,
    options
  ) => {
    const tamperedSize =
      regionSize < options.MIN_SIZE
        ? options.MIN_SIZE
        : regionSize > options.MAX_SIZE
        ? options.MAX_SIZE
        : regionSize;
    const rows =
      typeof totNodesInDepth !== "undefined"
        ? totNodesInDepth
        : parseInt(Math.sqrt(regionSize));
    const xIndex = nodeIndex % rows;
    const yIndex =
      typeof nodeDepth !== "undefined" ? nodeDepth : parseInt(nodeIndex / rows);
    return {
      y: center.y + yIndex * tamperedSize,
      x: center.x + xIndex * tamperedSize,
    };
  };

  partitionateRegion = (groups = [], options, nodes) => {
    const partitions = {};
    let previousSize = 0;
    let previousPosition = 0;
    Object.keys(groups).forEach((key, index) => {
      let filteredNodes = [...nodes];
      filteredNodes = filteredNodes.filter((node) => {
        return node.group === key;
      });
      const minimumX = _.minBy(filteredNodes, (o) => {
        return o.x;
      });
      const maximummX = _.maxBy(filteredNodes, (o) => {
        return o.x;
      });
      const maxDepthSizeOfGroup =
        maximummX && minimumX ? parseInt((maximummX.x + minimumX.x) / 2) : null;
      const cellSize = options.MIN_SIZE * previousSize + previousPosition;
      partitions[key] = {
        x: cellSize,
        y: index % 2 === 0 ? 0 : options.DISTRICT_DISTANCE,
      };
      previousPosition = cellSize;
      previousSize = maxDepthSizeOfGroup
        ? maxDepthSizeOfGroup + 1
        : parseInt(Math.sqrt(groups[key].size)) + 1;
      return partitions;
    });
    return partitions;
  };

  maxDepthSizeOfGroup = (nodes) => {
    let maxDepthSizeOfGroup = 0;
    const depths = [...new Set(nodes.map((obj) => obj.depth))];
    if (depths.length === 0) return undefined;
    let filteredNodes = null;
    for (const depth in depths) {
      filteredNodes = [...nodes];
      let countNodesInDepth = filteredNodes.filter((node) => {
        return node.depth === parseInt(depth);
      }).length;

      maxDepthSizeOfGroup =
        maxDepthSizeOfGroup <= countNodesInDepth
          ? countNodesInDepth
          : maxDepthSizeOfGroup;
    }
    return maxDepthSizeOfGroup;
  };

  computeNodesPositionsDistricts = (nodes, groups, options, relationships) => {
    const groupsNodes = {};
    const groupsRegions = {};

    const partitionsCenters = this.partitionateRegion(groups, options, nodes);
    for (const node of nodes) {
      const group = node?.group;
      const nodeId = node?.id;

      if (!groupsNodes[group]) groupsNodes[group] = [];
      groupsNodes[group].push(nodeId);

      if (!groupsRegions[group]) {
        groupsRegions[group] = {
          center: partitionsCenters[group],
          size: 1,
        };
      } else {
        groupsRegions[group].size++;
      }
    }

    for (const node of nodes) {
      if (!relationships.some((relationship) => relationship.type === "GEN")) {
        const nodePos = this.getNodePoint(
          groupsRegions[node.group].center,
          groupsRegions[node.group].size,
          node.depth
            ? this.calculateIndexByDepth(nodes, node)
            : groupsNodes[node.group].indexOf(node.id),
          this.getNumberOfNodesInDepth(nodes, node.group, node.depth),
          node.depth,
          options
        );
        node.x = nodePos.x;
        node.y = nodePos.y;
      } else {
        node.x =
          node.x * options.MIN_SIZE + groupsRegions[node.group].center.x * 2;
        node.y =
          node.y * options.MIN_SIZE + groupsRegions[node.group].center.y * 2;
      }
    }

    return nodes;
  };

  getNumberOfNodesInDepth = (nodes, group, depth) => {
    const getNodes = [...nodes];
    let numberOfNodesByDepth = getNodes.filter((node) => {
      return node.group === group && node.depth === depth;
    }).length;
    if (depth === undefined) return undefined;
    return numberOfNodesByDepth;
  };

  calculateIndexByDepth = (objects, object) => {
    let index = 0;
    for (const element of objects) {
      if (element.depth === object.depth) {
        if (element === object) {
          return index;
        }
        index++;
      }
    }
    return -1;
  };

  figureOutArrows = (relationship) => {
    let images = { type: "image", imageHeight: 32, imageWidth: 32 };
    let arrows = { to: { enabled: false }, from: { enabled: false } };
    if (relationship.type === "ASS") {
      // diamonds and/or no-end types
      if (
        relationship.primaryCubeCardinality === "n" ||
        relationship.primaryCubeCardinality === "N" ||
        relationship.primaryCubeCardinality === "*"
      ) {
        if (relationship.primaryCubeMandatoriness === false) {
          arrows.from = { ...images, src: many, scaleFactor: 1, enabled: true };
        } else {
          arrows.from = {
            ...images,
            src: oneToMany,
            scaleFactor: 1,
            enabled: true,
          };
        }
      }
      if (
        relationship.foreignCubeCardinality === "n" ||
        relationship.foreignCubeCardinality === "N" ||
        relationship.foreignCubeCardinality === "*"
      ) {
        if (relationship.foreignCubeMandatoriness === false) {
          arrows.to = { ...images, src: many, scaleFactor: 1, enabled: true };
        } else {
          arrows.to = {
            ...images,
            src: oneToMany,
            scaleFactor: 1,
            enabled: true,
          };
        }
      }
      if (relationship.primaryCubeCardinality === "1") {
        if (relationship.primaryCubeMandatoriness === true) {
          arrows.from = {
            ...images,
            src: oneToOne,
            scaleFactor: 1,
            enabled: true,
          };
        } else {
          arrows.from = { ...images, src: one, scaleFactor: 1, enabled: true };
        }
      }
      if (relationship.foreignCubeCardinality === "1") {
        if (relationship.foreignCubeMandatoriness === true) {
          arrows.to = {
            ...images,
            src: oneToOne,
            scaleFactor: 1,
            enabled: true,
          };
        } else {
          arrows.to = { ...images, src: one, scaleFactor: 1, enabled: true };
        }
      }
    } else if (relationship.type === "GEN") {
      arrows.from = { type: "triangle", scaleFactor: 1, enabled: true };
      arrows.to = { type: "triangle", scaleFactor: 1, enabled: false };
    } else {
      // no-end types
    }
    return arrows;
  };

  getRandomColor = () => {
    //creating random colors to display each groups
    let letters = "56789ABCD";
    let color = "#";
    for (let i = 0; i < 6; i++) {
      let typedArray = new Uint32Array(1);
      crypto.getRandomValues(typedArray);
      color += letters[Math.floor(typedArray[0] * Math.pow(2, -32) * 9)];
    }
    return color;
  };

  ColorLuminance = (hex, lum) => {
    // validate hex string
    hex = String(hex).replace(/[^0-9a-f]/gi, "");
    if (hex.length < 6) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    lum = lum || 0;

    // convert to decimal and change luminosity
    let rgb = "#",
      c,
      i;
    for (i = 0; i < 3; i++) {
      c = parseInt(hex.substr(i * 2, 2), 16);
      c = Math.round(Math.min(Math.max(0, c + c * lum), 255)).toString(16);
      rgb += ("00" + c).substr(c.length);
    }

    return rgb;
  };

  lightenDarkenColor = (col, amt) => {
    col = parseInt(col, 16);
    return (
      ((col & 0x0000ff) + amt) |
      ((((col >> 8) & 0x00ff) + amt) << 8) |
      (((col >> 16) + amt) << 16)
    ).toString(16);
  };

  getDistinctGroups = (nodes) => {
    //creating networkGroups object representing each uniqueGroups with custom attributes
    const networkGroups = {};
    const uniqueGroups = [...new Set(nodes.map((obj) => obj.group))];
    if (this.groups !== null) {
      if (
        JSON.stringify(uniqueGroups) ===
        JSON.stringify(Object.keys(this.groups))
      ) {
        return this.groups;
      }
    }
    for (const element of uniqueGroups) {
      const key = element;
      let color =
        nodes.find((node) => {
          return node.group === element;
        }).colour || this.getRandomColor();
      color = color.toLowerCase();
      const borderColor = this.ColorLuminance(color, -0.2);
      const selectedBorderColor = this.ColorLuminance(borderColor, -0.2);
      const size = nodes.filter((node) => node.group === key).length;
      networkGroups[key] = {
        color: {
          background: color,
          border: color,
          highlight: {
            border: selectedBorderColor,
            background: borderColor,
          },
          hover: borderColor,
        },
        borderWidth: 2,
        size: size,
      };
    }
    return networkGroups;
  };

  createNodeHierarchy = (nodes, relationships, groups) => {
    // creating Generalisation hierarchy between each node
    const genRelationships = relationships.filter(
      (relationship) => relationship.type === "GEN"
    );
    let parentId = "";
    //Adding ParentId attribute of each node if needed
    nodes.map((node) => {
      parentId = genRelationships
        .filter((genRelationship) => genRelationship.to === node.id)
        .map((o) => {
          return o.from;
        });
      if (parentId.length > 0) {
        node.Parent = parentId[0];
      } else node.Parent = "";
      parentId = "";
      return null;
    });
    const newNodes = [...nodes];
    let nodesFromGroup = [];
    let nodesToReturn = {};
    let allNodes = [];

    Object.keys(groups).forEach((key) => {
      nodesFromGroup = newNodes.filter((node) => node.group === key);
      nodesFromGroup = this.convertToHierarchy(nodesFromGroup);
      for (const node of nodesFromGroup) {
        //Adding a depth attribute to all node that represent the level in the hierarchy
        this.addDepthAttribute(node, 0, newNodes);
      }
      nodesToReturn[key] = nodesFromGroup;
    });
    Object.keys(groups).forEach((key) => {
      this.buchheim(nodesToReturn[key]);
      for (const node of nodesToReturn[key]) {
        // Flat list of node is needed to display nodes in graph
        this.hierarchyToList(node, allNodes);
      }
      return null;
    });
    return allNodes;
  };

  addDepthAttribute = (obj, depth = 0, newNodes) => {
    Object.keys(obj).forEach((key) => {
      if (key === "value") {
        obj[key].depth = depth;
        obj[key].childNumber = Object.keys(obj["children"]).length;
      }
      if (key === "children") {
        obj[key].forEach((child) => {
          this.addDepthAttribute(child, depth + 1, newNodes);
        });
      }
      return null;
    });
    return obj;
  };

  hierarchyToList = (objs, allNodes) => {
    Object.keys(objs).forEach((key) => {
      if (key === "value") allNodes.push(objs[key]);
      else if (key === "children") {
        objs[key].forEach((child) => {
          this.hierarchyToList(child, allNodes);
        });
      }
      return null;
    });
    return allNodes;
  };

  convertToHierarchy = (nodes) => {
    let nodeObjects = this.createStructure(nodes);

    for (let i = nodeObjects.length - 1; i >= 0; i--) {
      let currentNode = nodeObjects[i];

      // Skip over root node
      if (currentNode.value.Parent === "") {
        continue;
      }

      const parent = this.getParent(currentNode, nodeObjects);

      if (parent === null) {
        continue;
      }

      parent.children.push(currentNode);
      nodeObjects.splice(i, 1);
    }

    // What remains in nodeObjects will be the root nodes
    return nodeObjects;
  };

  createStructure = (nodes) => {
    let objects = [];

    for (const element of nodes) {
      objects.push({ value: element, children: [] });
    }
    return objects;
  };

  getParent = (child, nodes) => {
    let parent = null;
    for (const element of nodes) {
      if (element.value.id === child.value.Parent) {
        return element;
      } else if (element.children.length > 0) {
        parent = this.getParent(child, element.children);
        if (parent) return parent;
      }
    }
    return parent;
  };

  getNodes = (cubes, hierachical, relationships) => {
    if (!cubes || !cubes.length) return [];
    let nodes = [];
    const getVariables = (variables) => {
      if (!variables) return [];

      return variables
        .filter((variable) => variable.role === "D")
        .sort((a, b) => {
          if (a.orderNo !== b.orderNo) {
            return a.orderNo - b.orderNo;
          }
          return a.cubeVariableCode.localeCompare(b.cubeVariableCode);
        });
    };

    const getOtherVariables = (variables) => {
      if (!variables) return [];
      return variables
        .filter((variable) => variable.role !== "D")
        .sort((a, b) => {
          const roleComparison = b.role.localeCompare(a.role);
          if (roleComparison !== 0) return roleComparison;

          if (a.orderNo !== b.orderNo) return a.orderNo - b.orderNo;

          return a.cubeVariableCode.localeCompare(b.cubeVariableCode);
        });
    };

    cubes
      .filter((item) => this.visibleItems.includes(item.id))
      .forEach((item) => {
        let variables = getVariables(item.variables);
        let label = `<b>${item.cubeName}</b>\n<b>(${item.cubeCode})</b>`;
        let title = label;
        if (variables.length > 0) {
          label +=
            "\n\n" +
            variables
              .map((d) => "D: " + d.variableName + ` (${d.variableCode})`)
              .join("\n");
          title +=
            "\n\n" +
            variables
              .map((d) => "D: " + d.variableName + ` (${d.variableCode})`)
              .join("\n");
        }
        let otherVariables = getOtherVariables(item.variables);
        title = title.replaceAll("<br>", "\n");
        if (otherVariables.length > 0) {
          title +=
            "\n\n" +
            otherVariables
              .map(
                (d) => d.role + ":" + d.variableName + ` (${d.variableCode})`
              )
              .join("\n");
        }
        title = title.replaceAll("<b>", "");
        title = title.replaceAll("</b>", "");
        nodes.push({
          id: item.id,
          label: label,
          group: item.group,
          tid: item.tid,
          type: item.type,
          code: item.cubeCode,
          name: item.cubeName,
          title: title,
          colour: item.colour,
        });
      });
    const groups = this.getDistinctGroups(nodes);
    this.setGroups(groups);
    if (
      hierachical &&
      relationships.some((relationship) => relationship.type === "GEN")
    ) {
      nodes = this.createNodeHierarchy(nodes, relationships, groups);
    }
    if (hierachical) {
      this.computeNodesPositionsDistricts(
        nodes,
        groups,
        this.districtsOptions,
        relationships
      );
    }
    return nodes;
  };

  getEdges = (relationships) => {
    if (!relationships || !relationships.length) return [];
    const edges = [];
    const getTitle = (relationship) => {
      let title = "";
      title += `${relationship.name}\n`;
      title = title.replaceAll("<br>", "\n");
      title += `\n${relationship.description?.replaceAll(".", ".\n")}\n`;
      title += "\nJoin conditions:";
      if (
        Array.isArray(relationship.foreignCube) &&
        Array.isArray(relationship.primaryCube)
      ) {
        relationship.foreignCube.forEach((fc, index) => {
          const pc = relationship.primaryCube[index];
          const sourceName = relationship.sourceName;
          const destinationName = relationship.destinationName;
          if (!pc) return;
          title += `\n${sourceName}.${pc} = ${destinationName}.${fc}`;
        });
      }
      return title;
    };
    relationships
      .filter(
        (relationship) =>
          this.visibleItems.includes(relationship.from) &&
          this.visibleItems.includes(relationship.to)
      )
      .forEach((relationship) => {
        edges.push({
          from: relationship.from,
          to: relationship.to,
          arrows: this.figureOutArrows(relationship),
          arrowStrikethrough: true,
          endPointOffset: { from: 0, to: 0 },
          color: {
            color: relationship.type === "GEN" ? "#FF0000" : "#000000",
            hover: relationship.type === "GEN" ? "#FF0000" : "#000000",
            highlight: relationship.type === "GEN" ? "#FF0000" : "#000000",
          },
          width: 1,
          hoverWidth: 1.5,
          selectionWidth: 1,
          title: getTitle(relationship),
        });
      });
    return edges;
  };

  ////////////////////////////////////////////////
  buchheim = (roots) => {
    // Initialising parameter
    for (let root of roots) {
      this.dfs(root);
    }
    let x = 0;
    let y = 0;
    let biggestDepth = 0;
    for (let root of roots) {
      this.setInitialCoordinates(root, x, y);
      x += root.subtreeSize + 1;
      for (let node of root.children) {
        biggestDepth = Math.max(biggestDepth, node.depth);
      }
    }
    for (let root of roots) {
      this.layout(root, biggestDepth);
    }
    return roots;
  };

  dfs = (node) => {
    // calc depth
    node.depth = node.parent ? node.parent.depth + 1 : 0;
    // Calc subtreesize
    node.subtreeSize =
      node.children.length +
      node.children.reduce((sum, child) => sum + this.dfs(child), 0);
    return node.subtreeSize;
  };

  setInitialCoordinates = (node, x, y) => {
    node.value.x = x;
    node.value.y = y;
    if (node.children.length > 0) {
      let totalSize = node.children.reduce(
        (sum, child) => sum + child.subtreeSize,
        0
      );
      let step = (node.subtreeSize - totalSize) / node.children.length;
      for (let i = 0; i < node.children.length; i++) {
        let child = node.children[i];
        this.setInitialCoordinates(child, Math.floor(x + step), y + 1.5);
        x += child.subtreeSize + step;
      }
    }
  };

  layout = (node, biggestDepth) => {
    // Calc final coordinates
    for (let child of node.children) {
      this.layout(child, biggestDepth);
    }
    if (node.children.length > 0) {
      let leftMost = node.children[0].value.x;
      let rightMost = node.children[node.children.length - 1].value.x;
      node.value.x = (leftMost + rightMost) / 2;
    }
  };
}
